import { Button, FormHelperText } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { omit } from 'lodash-es';
import React, { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { Label } from '../Label/';

export interface IOptionInObject {
  options: { [key: string]: string }[];
  autoCompleteField: string;
}

export interface ITagsInput {
  initialValue: string[];
  value?: string | string[];
  onValueEnter?: (input: string) => Promise<void>;
  optionsInStringArray?: string[];
  optionsInObject?: IOptionInObject;
  onTagsChange(args: string[]): void;
  placeholder?: string;
  multiple?: boolean;
  freeSolo?: boolean;
  size?: string;
  name: string;
  showMandatorySymbol?: boolean;
  resetValue?: boolean;
}

const WsAutoComplete: React.FC<ITagsInput & TextFieldProps> = ({
  initialValue = [],
  value,
  onValueEnter,
  optionsInStringArray = [],
  optionsInObject,
  onTagsChange,
  placeholder,
  multiple,
  freeSolo = false,
  size = 'medium',
  name,
  showMandatorySymbol = false,
  variant = 'outlined',
  resetValue = false,
  ...props
}) => {
  const [tagsValue, setTagsValue] = React.useState<string[]>([]);

  const holder = placeholder ? placeholder : '';

  const { control } = useFormContext();

  const handleOptionsInObject = (options: IOptionInObject) => {
    return options.options.map(
      (option: { [key: string]: string }) => option[options ? options.autoCompleteField : 'text']
    );
  };

  useEffect(() => {
    if (resetValue) {
      setTagsValue([]);
      onTagsChange([]);
    }
  }, [resetValue]);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case ',':
      case '13': {
        event.preventDefault();
        event.stopPropagation();
        if ((event.target as HTMLInputElement).value.length > 0) {
          setTagsValue([...tagsValue, (event.target as HTMLInputElement).value]);
          onTagsChange([...tagsValue, (event.target as HTMLInputElement).value]);
        }
        break;
      }
      default:
        if (onValueEnter) {
          onValueEnter((event.target as HTMLInputElement).value);
        }
    }
  };

  const handleOnChange = (newValue: string | string[] | null) => {
    if (newValue) {
      if (typeof newValue === 'string') {
        setTagsValue([newValue]);
        onTagsChange([newValue]);
      } else {
        setTagsValue(newValue);
        onTagsChange(newValue);
      }
    } else {
      setTagsValue([]);
      onTagsChange([]);
    }
  };

  return (
    <>
      {props.label && (
        <Label>
          {props.label}
          {showMandatorySymbol ? ' *' : ''}
        </Label>
      )}
      <Controller
        name={name}
        control={control}
        render={({ fieldState: { error } }) => (
          <>
            <Autocomplete
              id={name}
              multiple={multiple}
              filterOptions={(filterOptions) => filterOptions}
              size={size}
              freeSolo={freeSolo}
              options={
                optionsInObject ? handleOptionsInObject(optionsInObject) : optionsInStringArray
              }
              defaultValue={initialValue}
              value={value ?? tagsValue}
              filterSelectedOptions
              renderOption={(props, option: string) =>
                option?.indexOf('(Add') > -1 ? (
                  <Button onClick={() => handleOnChange(option)}>{option}</Button>
                ) : (
                  <li {...props}>{option}</li>
                )
              }
              onChange={(event, newValue: string | string[] | null) => handleOnChange(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant={variant}
                  placeholder={holder}
                  onKeyPress={handleKeyPress}
                  {...omit(props, ['label'])}
                />
              )}
            />
            {error && <FormHelperText error>{error.message}</FormHelperText>}
          </>
        )}
      />
    </>
  );
};

export default WsAutoComplete;
